/**
 * @lodex-pro/shared/auth/permissions — Permission catalog + ROLE_PERMISSIONS map.
 *
 * Phase 3 / Plan 02 — D-06. Single source of truth for the master spec §6
 * permission matrix (24 permissions × 10 roles). Plan 06's permissionGuard
 * preHandler reads `ROLE_PERMISSIONS[request.roleCode].has(permission)` per
 * D-08; Plan 04's cognito-jwt plugin derives `request.permissions` from this
 * map at the end of auth resolution.
 *
 * Type-checked, fast (set lookup, no DB), single grep target. Adding a
 * permission = one PR with the const + the role assignments together;
 * type-system rejects assigning a permission that doesn't exist.
 *
 * Spec: master spec §6, CONTEXT D-06 / D-07.
 */

import { COMBINED_ROLE_CODES } from './role-codes.js';
import type { CombinedRoleCode, RoleCode, SingleRoleCode } from './role-codes.js';

/**
 * Permission const — all 24 rows of the master spec §6 matrix.
 *
 * Each key maps 1:1 to a row in the §6 table. Values equal their keys
 * (string-enum invariant) so `Permission.CREATE_JOB === 'CREATE_JOB'`.
 *
 * Qualifiers (`ltd`/`ro`/`own`) are a route-level concern handled by
 * PermissionResult predicates (D-07). Including a permission in a role's
 * Set means the role has AT LEAST that access; the predicate narrows it.
 */
export const Permission = {
  // === Tenant management (master spec §6) ===
  /** Row: "Manage all tenants" — PlatAdmin only */
  MANAGE_ALL_TENANTS: 'MANAGE_ALL_TENANTS',
  /** Row: "Access other tenant data" — PlatAdmin only (same gate as MANAGE_ALL_TENANTS) */
  MANAGE_TENANT: 'MANAGE_TENANT',
  /** Row: "Manage drivers/trucks" — PlatAdmin, TenAdmin, DispMgr */
  MANAGE_USERS: 'MANAGE_USERS',
  /** Row: "Import operators and sites" — PlatAdmin, TenAdmin */
  IMPORT_OPERATORS_AND_SITES: 'IMPORT_OPERATORS_AND_SITES',
  /** Row: "Manage operator portal access" — PlatAdmin only */
  MANAGE_OPERATOR_PORTAL_ACCESS: 'MANAGE_OPERATOR_PORTAL_ACCESS',
  /**
   * Operator portal rework PR 7 (0214) — create and edit the company's routes (their wells and
   * battery tanks) and select routes as a foreman. OpAdmin and Foreman (Corbin: foremen AND admins
   * build routes). A FOREMAN selects routes only for themself (checked in SQL).
   */
  MANAGE_ROUTES: 'MANAGE_ROUTES',
  /**
   * Operator portal rework PR 7 (0214) — the company's own tank records: confirm the proposed
   * matches of its haulers' tanks, link and unlink by hand, rename and deactivate. OpAdmin only.
   */
  MANAGE_OPERATOR_TANKS: 'MANAGE_OPERATOR_TANKS',
  /**
   * Operator portal rework PR 8 (0215) — choose the tanks one looks after (their contacts) and so
   * what one sees. A PUMPER or FOREMAN for themself, a FOREMAN for a pumper, an OPERATOR_ADMIN for
   * any pumper or foreman (checked in SQL). Design §1.1 called it SELECT_OWN_TANKS; renamed
   * because the admin and a foreman also choose on someone's behalf.
   */
  SELECT_OPERATOR_TANKS: 'SELECT_OPERATOR_TANKS',
  /**
   * Operator portal rework PR 10 (0217) — the requestor → pumper matches (design §6): list them,
   * settle a conflict (link / not them) and re-run the match. OpAdmin only.
   */
  MANAGE_REQUESTOR_LINKS: 'MANAGE_REQUESTOR_LINKS',
  /**
   * Operator portal rework PR 11c (0221) — the pumper's phone sends the tank picks and drops it
   * queued with no signal to POST /v1/operator-portal/sync/receive. PUMPER only, the one operator
   * role on the mobile app (design §8); separate from the driver's SYNC_PUSH so neither receiver
   * takes the other's queue.
   */
  OPERATOR_SYNC_PUSH: 'OPERATOR_SYNC_PUSH',
  /**
   * Phase 16 / Task 16.2 (DEC-157) — PLATFORM_ADMIN only. Approve/reject
   * disposal-registration submissions, list the admin queue, view detail,
   * and (by extension) manage disposal portal_status transitions in 16.3+.
   * Parallel to MANAGE_OPERATOR_PORTAL_ACCESS; NOT reusing that gate so audit
   * logs can distinguish disposal from operator admin actions cleanly.
   */
  MANAGE_DISPOSAL_PORTAL_ACCESS: 'MANAGE_DISPOSAL_PORTAL_ACCESS',
  /**
   * §58 feature gating — PLATFORM_ADMIN only. Set a hauler's template, its per-feature
   * overrides and trials, and (EXPRESS) its one customer. Not MANAGE_PLATFORM_CATALOG:
   * what a hauler may use is a commercial decision, not catalog upkeep.
   */
  MANAGE_HAULER_FEATURES: 'MANAGE_HAULER_FEATURES',
  /**
   * Paelode team page — PLATFORM_ADMIN only. Invite another Paelode admin, resend their invite,
   * suspend or reactivate them (/v1/platform/team). Its own code, not MANAGE_USERS: who can make
   * a platform admin must never widen by accident when a tenant role gains MANAGE_USERS.
   */
  MANAGE_PLATFORM_TEAM: 'MANAGE_PLATFORM_TEAM',
  /**
   * Operator portal rework PR 9 (0213) — read the operator API keys that read this hauler's data.
   * TenAdmin only. Read-only: issuing, rotating and revoking moved to Paelode and the operator
   * admin (Corbin, requirement 6); the hauler is told to contact Paelode. Replaces
   * ISSUE_OPERATOR_API_CREDENTIALS.
   */
  VIEW_OPERATOR_API_CREDENTIALS: 'VIEW_OPERATOR_API_CREDENTIALS',
  /**
   * Operator portal rework PR 9 (0213) — issue, rotate and revoke the company's operator API keys
   * from the operator portal. OpAdmin only. Paelode does the same under
   * MANAGE_OPERATOR_PORTAL_ACCESS.
   */
  MANAGE_OPERATOR_API_KEYS: 'MANAGE_OPERATOR_API_KEYS',
  /**
   * Operator webhooks PR 1 (0239) — add, change, rotate and remove the company's webhook
   * endpoints, and (PR 3) read their delivery log. OpAdmin only.
   */
  MANAGE_OPERATOR_WEBHOOKS: 'MANAGE_OPERATOR_WEBHOOKS',
  /**
   * Operator Reports (b) (0257) — the company's scheduled emailed reports: who gets them, how
   * often, what is in them. OpAdmin only.
   */
  MANAGE_OPERATOR_REPORT_SCHEDULES: 'MANAGE_OPERATOR_REPORT_SCHEDULES',

  // === Job lifecycle (master spec §6, §8) ===
  /** Row: "Create/assign jobs" — PlatAdmin, TenAdmin, DispMgr, Disp; "Create load (mobile)" — Driver */
  CREATE_JOB: 'CREATE_JOB',
  /** Row: "Create/assign jobs" — PlatAdmin, TenAdmin, DispMgr, Disp */
  ASSIGN_JOB: 'ASSIGN_JOB',
  /**
   * #60 / DEC-365 — change a job's pickup site (same customer only) up to approval.
   * PlatAdmin, TenAdmin, DispMgr, Disp, TruckPush, BillMgr. A driver changes their own job's site
   * through sync (SYNC_PUSH + the ownership guard), so DRIVER has no code here.
   */
  CHANGE_JOB_SITE: 'CHANGE_JOB_SITE',
  /** Row: "Cancel job" — PlatAdmin ✓, TenAdmin ✓, DispMgr ✓, Disp ltd */
  CANCEL_JOB: 'CANCEL_JOB',
  /** Row: "Complete job on behalf of driver" — PlatAdmin, TenAdmin, DispMgr */
  COMPLETE_JOB: 'COMPLETE_JOB',
  /** Row: "Reject job (driver)" — Driver only */
  REJECT_JOB: 'REJECT_JOB',
  /** Row: "Override job completion protection" — PlatAdmin, TenAdmin, DispMgr */
  OVERRIDE_COMPLETION_PROTECTION: 'OVERRIDE_COMPLETION_PROTECTION',
  /**
   * Phase 6 / CONTEXT D-13 + D-14 — dispatcher force-close authority for a
   * driver's paused/running job chain via POST /v1/jobs/:id/dispatcher-close.
   * Writes a STOPPED timeclock_event with triggered_by='DISPATCHER_CLOSED' and
   * a mandatory reason (>= 8 chars). NOT granted to DISPATCHER — they must
   * escalate to DISPATCH_MANAGER+; mirrors the Phase 5 JOB_CANCEL_WITH_EVIDENCE
   * pattern. NOT granted to DRIVER (drivers stop their own chain via the
   * normal timeclock route).
   */
  JOB_FORCE_CLOSE: 'JOB_FORCE_CLOSE',

  // === Run-ticket capture + review (master spec §6, §11) ===
  /** Row: "Capture run ticket" — PlatAdmin, Driver */
  CAPTURE_RUN_TICKET: 'CAPTURE_RUN_TICKET',
  /** Row: "Review ticket (InReview)" + "Approve tickets for invoicing" — PlatAdmin, TenAdmin, DispMgr, TruckPush, BillMgr */
  REVIEW_TICKET: 'REVIEW_TICKET',
  /** Row: "Edit completed ticket volume" — PlatAdmin, TenAdmin, TruckPush, BillMgr */
  EDIT_TICKET_VOLUME: 'EDIT_TICKET_VOLUME',
  /** Row: "View run tickets" — all roles (ro/ltd/own qualifiers handled per D-07) */
  VIEW_RUN_TICKETS: 'VIEW_RUN_TICKETS',
  /** Row: "Acknowledge GPS flag" — PlatAdmin, TenAdmin, DispMgr, TruckPush (inferred from review flow §11) */
  ACKNOWLEDGE_GPS_FLAG: 'ACKNOWLEDGE_GPS_FLAG',

  // === Billing (master spec §6, §12) ===
  /** Row: "Generate/send invoices" — PlatAdmin ✓, TenAdmin ✓, BillMgr ✓, BillStaff ltd */
  CREATE_INVOICE: 'CREATE_INVOICE',
  /** Row: "View invoices / run tickets" (billing view) — PlatAdmin, TenAdmin, BillMgr, BillStaff, OpAdmin */
  VIEW_INVOICES: 'VIEW_INVOICES',
  /** QuickBooks Online integration per master spec §12 — PlatAdmin, TenAdmin, BillMgr */
  CONNECT_QBO: 'CONNECT_QBO',
  /**
   * DEC-326 item 7 (Corbin, 2026-10-13) — edit the company default payment terms
   * (Net 30) and each customer's override. TenAdmin + BillMgr; NOT BillStaff. The
   * combined office roles inherit it through their BILLING_MANAGER component.
   */
  MANAGE_PAYMENT_TERMS: 'MANAGE_PAYMENT_TERMS',
  /** Phase 28 (DEC-331 D-9): disposal prices, the recommendation switch and display modes —
   *  PlatAdmin, TenAdmin, BillMgr (the same three as CONNECT_QBO). */
  MANAGE_DISPOSAL_PRICES: 'MANAGE_DISPOSAL_PRICES',
  /**
   * gap 1 / DEC-383 (Corbin 2026-10-30, D-18 item 4, D-19) — edit customer prices: customer x job-type prices,
   * the customer prices tab and the hauler-wide bulk page. PlatAdmin, TenAdmin, BillMgr, BillStaff; the combined
   * office roles inherit it through their billing half. NOT DispMgr/TruckPush/Dispatcher. OPEN-213 also gates
   * zones, zone memberships, zone prices and the CUSTOMER rows of customer_disposal_fees with it (DEC-383).
   * Site DEFAULT fees, hauler cost prices and the "sees our cost" read stay on MANAGE_DISPOSAL_PRICES.
   */
  MANAGE_CUSTOMER_PRICES: 'MANAGE_CUSTOMER_PRICES',
  /**
   * DEC-356 (#50, Corbin 2026-10-15) — edit an approved (TO_BE_INVOICED) ticket's billed
   * lines before it is on any invoice: volume, hours, extras and product quantities, with a
   * reason and an audit row. PlatAdmin, TenAdmin, BillMgr, BillStaff; the combined office
   * roles inherit it through their billing half. NOT TruckPush or DispMgr, though they edit
   * volume in In Review (EDIT_TICKET_VOLUME).
   */
  EDIT_BILLING_TICKET: 'EDIT_BILLING_TICKET',
  /**
   * OPEN-217 / DEC-382 (Corbin, 2026-10-30) — create, edit and delete the per-job-type rules on
   * what a driver types into a ticket (Warning / Block). PlatAdmin, TenAdmin, BillMgr, BillStaff;
   * the combined office roles inherit it through their billing half. NOT DispMgr, Dispatcher or
   * Driver.
   */
  MANAGE_VALIDATION_RULES: 'MANAGE_VALIDATION_RULES',
  /**
   * OPEN-156 (Corbin, 2026-10-19, option a) — void an approved (TO_BE_INVOICED) ticket that is on
   * no live invoice: it is cancelled, its unbilled lines are removed, with a reason and an audit
   * row. TenAdmin + BillMgr only (the combined billing-manager roles inherit it); NOT PlatAdmin,
   * BillStaff, DispMgr or TruckPush. There is no un-void.
   */
  VOID_BILLING_TICKET: 'VOID_BILLING_TICKET',
  /** Row: "Generate platform invoices" — PlatAdmin only */
  GENERATE_PLATFORM_INVOICES: 'GENERATE_PLATFORM_INVOICES',

  // === Dispatch (master spec §6, §9) ===
  /** Row: "View live driver map" — PlatAdmin, TenAdmin, DispMgr, Disp, TruckPush(ro), Driver(own) */
  VIEW_DRIVER_MAP: 'VIEW_DRIVER_MAP',
  /** Geofence management per master spec §9 — PlatAdmin, TenAdmin, DispMgr */
  EDIT_GEOFENCE: 'EDIT_GEOFENCE',

  // === Audit + compliance (master spec §6, §32) ===
  /** Row: "View audit trail" — PlatAdmin, TenAdmin, DispMgr, Disp(ltd), TruckPush(ro), BillMgr(ro), BillStaff(ro), Driver(own), OpAdmin */
  VIEW_AUDIT_TRAIL: 'VIEW_AUDIT_TRAIL',
  /** Row: "Generate OCC/RRC reports" — PlatAdmin, TenAdmin, BillMgr, BillStaff(ro), OpAdmin, Foreman(ro) */
  GENERATE_COMPLIANCE_REPORT: 'GENERATE_COMPLIANCE_REPORT',

  // === Operator portal (master spec §6, §15) ===
  /** Row: "Submit haul requests" — PlatAdmin, OpAdmin, Foreman */
  SUBMIT_HAUL_REQUEST: 'SUBMIT_HAUL_REQUEST',
  /** Row: "View tank levels" — PlatAdmin, OpAdmin, Foreman */
  VIEW_TANK_LEVELS: 'VIEW_TANK_LEVELS',

  // === Lifecycle events + disposal flow (Phase 06.1 / DEC-027) ===
  /**
   * Phase 06.1 / DEC-027 — DRIVER posts arbitrary lifecycle events
   * (SHIFT_STARTED, JOB_ACCEPTED, EN_ROUTE_TO_PICKUP, ARRIVED_PICKUP,
   * GAUGE_ENTRY_STARTED, audit-only events) into `job_events`. The heavier
   * state-machine-driving disposal transitions (ARRIVED_DISPOSAL →
   * DISPOSAL_COMPLETE) use the dedicated TRANSITION_TO_DISPOSAL permission
   * so the role grants stay split and explicit.
   */
  RECORD_JOB_EVENT: 'RECORD_JOB_EVENT',
  /**
   * Phase 06.1 / DEC-027 — DRIVER advances ARRIVED_DISPOSAL →
   * DISPOSAL_COMPLETE via POST /v1/jobs/:id/transitions/disposal-*. Separate
   * from RECORD_JOB_EVENT because the state-machine side-effect is heavier
   * (writes `jobs.status_id` + `jobs.actual_disposal_facility_id`); the
   * split prevents future role grants for "just events" from accidentally
   * granting state-machine writes.
   */
  TRANSITION_TO_DISPOSAL: 'TRANSITION_TO_DISPOSAL',
  /**
   * Phase 06.1 / DEC-027 — read the platform-managed `disposal_facilities`
   * catalog. Drivers read for DisposalScreen + facility override picker;
   * dispatchers and management roles read for assignment UI. The table is
   * platform-managed (no RLS — see master spec §49); the gate is a
   * lightweight role check.
   */
  VIEW_DISPOSAL_FACILITIES: 'VIEW_DISPOSAL_FACILITIES',

  // === Phase 06.2 additions ===
  /** PLATFORM_ADMIN only — create/update entries in the platform-managed site_types catalog. */
  MANAGE_SITE_TYPES: 'MANAGE_SITE_TYPES',
  /** Every role — read the site_types catalog for assignment UI + job detail projection. */
  READ_SITE_TYPES: 'READ_SITE_TYPES',
  /** TENANT_ADMIN only — create/update service_charge rate rows for the tenant (POST/PUT). */
  MANAGE_SERVICE_CHARGES: 'MANAGE_SERVICE_CHARGES',
  /** Every role — read service_charge rates for rate display + invoice calculation (GET). */
  VIEW_SERVICE_CHARGES: 'VIEW_SERVICE_CHARGES',

  // === Wave 3 / Quick 260615-uf9 — reference-data management ===
  /**
   * Wave 3 — create/deactivate the tenant's operators, company_sites, and
   * site_tanks from the dispatcher Management console. Granted to
   * DISPATCH_MANAGER, TENANT_ADMIN, PLATFORM_ADMIN (NOT plain DISPATCHER —
   * mirrors the EDIT_GEOFENCE management-only posture). Reads stay on
   * CREATE_JOB / VIEW_RUN_TICKETS; only the mutations carry this gate.
   */
  MANAGE_REFERENCE_DATA: 'MANAGE_REFERENCE_DATA',

  // === Fleet management (Quick 260616-flt / DEC-067) ===
  /**
   * Fleet UI — create/edit/deactivate the tenant's trucks, trailers, and yards,
   * and edit driver compliance/assignment fields, from the dispatcher Fleet
   * console. Granted to DISPATCH_MANAGER, TENANT_ADMIN, PLATFORM_ADMIN (NOT plain
   * DISPATCHER — mirrors the MANAGE_REFERENCE_DATA management-only posture). The
   * heavier driver lifecycle actions (deactivate-disables-Cognito, yard transfer,
   * invite SMS) are a deferred follow-up and are NOT covered by this permission yet.
   */
  MANAGE_FLEET: 'MANAGE_FLEET',

  // Phase 06.2 note: the 5 new transition endpoints (start-job, arrived-pickup,
  // start-work, start-unload, disposal-out) REUSE the existing
  // RECORD_JOB_EVENT permission rather than introducing a sibling. Same
  // DRIVER-only grant; the 4-guard pattern is identical to the events endpoint.

  // === Phase 07 / offline sync (DEC-034 / DEC-037) ===
  /**
   * Phase 07 / DEC-034 — DRIVER pushes a batch of queued offline writes to
   * POST /v1/sync/receive. Driver-only: non-driver roles never accumulate an
   * offline write queue and must not be able to inject unvalidated payloads
   * into the sync receiver. Mirrors the RECORD_JOB_EVENT grant pattern.
   */
  SYNC_PUSH: 'SYNC_PUSH',
  /**
   * Phase 07 / DEC-037 P3 — DRIVER (and PLATFORM_ADMIN for admin/testing
   * parity) pulls the §16b reference data package (job_statuses catalog,
   * disposal_facilities, site_types, service_charges) via GET /v1/sync/pull.
   * Separate from SYNC_PUSH so pull-only consumers (future) can be granted
   * read access without gaining push capability.
   */
  SYNC_PULL_REFERENCE_DATA: 'SYNC_PULL_REFERENCE_DATA',

  // === Phase 24 / Task 24.2 (DEC-092) — platform catalog sync ===
  /**
   * PLATFORM_ADMIN only — view catalog-sync health + manage the platform
   * operators/wells catalog and the state-agency sync config/review queue.
   * Same posture as MANAGE_SITE_TYPES (Paelode-internal, platform-level).
   */
  MANAGE_PLATFORM_CATALOG: 'MANAGE_PLATFORM_CATALOG',

  // === Phase 21 / Task 21.1 (DEC-327) — Paelode Systems' own billing ===
  /**
   * PLATFORM_ADMIN only — rate cards, customer subscriptions, billing accounts and the
   * platform billing settings (sender, prefixes, terms). Its own gate rather than
   * MANAGE_PLATFORM_CATALOG so a future catalog-only staff role cannot see money
   * (21-CONTEXT.md section 5). Routes ALSO require the caller's active tenant to be the
   * PLATFORM tenant, and run under withTenant(<that tenant>) so RLS is the third layer.
   */
  MANAGE_PLATFORM_BILLING: 'MANAGE_PLATFORM_BILLING',

  // === #56 (DEC-362) — the Paelode admin ops console ===
  /**
   * PLATFORM_ADMIN only — read the ops console: Health tiles, the Errors list and the
   * refCode / errorId lookup across every tenant. Its own gate rather than
   * MANAGE_PLATFORM_CATALOG (D-11). Routes ALSO require the PLATFORM active tenant, and the
   * database reads assert it again (42501 otherwise).
   */
  VIEW_PLATFORM_OPS: 'VIEW_PLATFORM_OPS',
  /**
   * PLATFORM_ADMIN only — edit ops alert rules and recipients (#56 PR 2). Declared with
   * VIEW_PLATFORM_OPS so the role matrix changes once.
   */
  MANAGE_PLATFORM_ALERTS: 'MANAGE_PLATFORM_ALERTS',

  // === Phase 24 / Task 24.3 (DEC-094) — hauler catalog junctions ===
  /**
   * DISPATCHER+ — read the hauler's authorized working set (hauler_operators,
   * tank_batteries + linked wells/tanks, hauler_disposal_facilities) AND search
   * the read-only platform catalog (platform_operators, wells, standard tank
   * catalog) from the dispatcher "Sites & Operators" console. Read-only; the
   * mutations carry MANAGE_HAULER_CATALOG. Mirrors the VIEW_SERVICE_CHARGES /
   * READ_SITE_TYPES read posture (granted down to plain DISPATCHER).
   */
  VIEW_HAULER_CATALOG: 'VIEW_HAULER_CATALOG',
  /**
   * DISPATCH_MANAGER+ — create/update the hauler's catalog junctions: add
   * operators, create tank batteries, link/unlink wells, add/edit/deactivate
   * tanks, authorize disposal wells. Management-only (NOT plain DISPATCHER) —
   * mirrors the MANAGE_REFERENCE_DATA / MANAGE_FLEET posture. The
   * TENANT_ADMIN-only hauler-operator soft delete is gated separately on
   * MANAGE_TENANT.
   */
  MANAGE_HAULER_CATALOG: 'MANAGE_HAULER_CATALOG',

  // === Phase 16 / Task 16.4 (DEC-161) — disposal daily email module ===
  /**
   * DISPOSAL_ADMIN only — CRUD on the multi-recipient list backing
   * `module_daily_email` (DEC-005 / DEC-158). Add / list / soft-delete
   * (opted_out) email addresses for either a single facility (site-level
   * recipient) or the consolidated digest (facility_id NULL, corporate/
   * accounting). NOT granted to hauler-side TENANT_ADMIN — disposal admins
   * manage their own portal notification distribution list.
   */
  MANAGE_DISPOSAL_EMAIL_RECIPIENTS: 'MANAGE_DISPOSAL_EMAIL_RECIPIENTS',

  // === Phase 16 / Task 16.5 (DEC-162) — disposal custom report module ===
  /**
   * DISPOSAL_ADMIN only — CRUD on report templates + report recipients +
   * trigger async report generation. Backs `module_custom_reports`
   * (DEC-005 / DEC-158). SEPARATE from MANAGE_DISPOSAL_EMAIL_RECIPIENTS
   * (16.4) so a future admin sub-role split can gate reports and
   * daily-email independently (DEC-162 Q3). Public unsubscribe endpoint is
   * unauthenticated (HMAC-signed token) — no permission gate.
   */
  MANAGE_DISPOSAL_REPORTS: 'MANAGE_DISPOSAL_REPORTS',

  // === Phase 16 / Task 16.6 (DEC-163) — disposal BI panels MVP ===
  /**
   * DISPOSAL_ADMIN only — read the three BI panel endpoints
   * (volume-trends, acceptance-by-site, data-export). Backs the 3-of-6
   * MVP set of §55 module toggles on disposal_companies
   * (module_volume_trends, module_acceptance_by_site, module_data_export).
   *
   * SEPARATE from MANAGE_DISPOSAL_REPORTS (16.5): BI panel viewing is
   * per-tenant self-service (a DISPOSAL_ADMIN loading the dashboard for
   * their own company), distinct from admin-managed report scheduling.
   * Keeping the permissions crisp lets a future viewer-only sub-role gate
   * BI reads without gating report scheduling.
   */
  VIEW_DISPOSAL_BI_PANELS: 'VIEW_DISPOSAL_BI_PANELS',

  // === OPEN-037 — shared-device roster refresh ===
  /**
   * Read the shared-device driver picker list for YOUR OWN tenant:
   * `GET /v1/devices/:id/roster` → `{ userId, fullName, phoneLast4, status }[]`.
   * Granted to DRIVER plus the three roles that already held `MANAGE_USERS`
   * (PLATFORM_ADMIN, TENANT_ADMIN, DISPATCH_MANAGER), which was this route's
   * previous gate.
   *
   * Why DRIVER holds it (OPEN-037, Corbin 2026-10-01 — option B)
   * ------------------------------------------------------------
   * A shared tablet's roster only ever got rows at QR enrollment. Refreshing it
   * needed `MANAGE_USERS`, and the roster screen is by definition the screen
   * where NOBODY is signed in yet — so a driver hired after enrollment never
   * appeared on the tablet, and a driver removed from the tenant never
   * disappeared. The refresh now runs while a driver IS signed in, on that
   * driver's own token. After any one existing driver signs in, the new hire is
   * on the picker.
   *
   * This is NOT the dispatcher's driver roster. Quick 260613 withheld
   * `VIEW_DRIVER_MAP` from DRIVER so a driver cannot enumerate other drivers'
   * live POSITIONS; that still holds and is untouched here. This permission
   * carries name + phone-last-4 + status and nothing else — the same fields the
   * tablet already renders to anyone standing in front of it before sign-in, so
   * the marginal disclosure to a driver who is holding that tablet is ~zero.
   *
   * Scope is the caller's own tenant, enforced the way every other tenant read
   * is: `req.tenantId` from the verified JWT plus RLS on `tenant_users`. The
   * `:id` device UUID in the path is NOT a credential and is not verified —
   * nothing is persisted server-side at enrollment to verify it against (see
   * the route's header). It stays in the path for client-side audit correlation.
   */
  READ_DEVICE_ROSTER: 'READ_DEVICE_ROSTER',

  // === OPEN-036 / DEC-335 — shared-device remote clear ===
  /**
   * List this tenant's enrolled shared tablets and end a tablet's session from the web
   * (`GET /v1/devices`, `POST /v1/devices/:id/clear`). Master spec §7: "Remote clear:
   * Dispatcher can force-logout from web console" — so DISPATCHER holds it, which it
   * would not if this rode on MANAGE_USERS. Also PLATFORM_ADMIN, TENANT_ADMIN and
   * DISPATCH_MANAGER (Corbin, 2026-10-14).
   *
   * What it can do is narrow: it signs out whoever is on the tablet at its next check-in.
   * It does not revoke the driver's account, touch their personal phone, or discard the
   * tablet's queued tickets. Own tenant only (req.tenantId + RLS on shared_devices).
   */
  CLEAR_SHARED_DEVICE: 'CLEAR_SHARED_DEVICE',

  // === Phase 22 / Task 22.2 (DEC-230 / DEC-231) — push device registration ===
  /**
   * Bind THIS device's push token to the signed-in user in the active tenant
   * (`PUT /v1/push/registration`) and release that binding
   * (`DELETE /v1/push/registration/:installationId`). DRIVER only: the v1 push
   * events all go to drivers (DEC-228), and DRIVER is the only tenant role that
   * signs in on the mobile app. User and tenant always come from the verified
   * request, never the body. A release can only touch the caller's own binding.
   */
  REGISTER_PUSH_DEVICE: 'REGISTER_PUSH_DEVICE',
  /**
   * Phase 22 / Task 22.9 (DEC-233) — read and change YOUR OWN push preferences
   * (`GET/PUT /v1/users/me/notification-preferences`). DRIVER only: the v1 push events all
   * go to drivers, so no other role has anything to opt out of. User and tenant come from
   * the verified request; a caller can never read or change another user's preferences.
   * Mandatory events (assigned / cancelled / reassigned away) cannot be turned off.
   */
  MANAGE_OWN_NOTIFICATION_PREFS: 'MANAGE_OWN_NOTIFICATION_PREFS',
} as const;

export type PermissionCode = (typeof Permission)[keyof typeof Permission];

/**
 * ROLE_PERMISSIONS — master spec §6 matrix encoded.
 *
 * The §6 grid is the SINGLE source of truth. Each role's Set lists the
 * permissions that role holds. Qualifier predicates (`ltd`/`ro`/`own`) are
 * a per-route concern: including a permission in a Set means the role has
 * AT LEAST that access; Plan 06+ predicates narrow it at the route level via
 * PermissionResult (D-07).
 *
 * `as const satisfies Record<SingleRoleCode, ReadonlySet<PermissionCode>>`
 * enforces compile-time exhaustiveness: every single RoleCode key must be
 * present and every Set value must be a valid PermissionCode. Adding a role =
 * TS error until this map is updated. Adding a permission typo = TS error
 * immediately.
 *
 * DEC-313: this literal holds the ELEVEN AUTHORED roles. The four combined
 * office roles are derived from it below (ROLE_PERMISSIONS), so they are never
 * authored by hand and cannot drift from their components.
 */
const SINGLE_ROLE_PERMISSIONS = {
  /**
   * PLATFORM_ADMIN — Paelode Systems internal. Full cross-tenant access.
   * §6 column 1 (PlatAdmin).
   */
  PLATFORM_ADMIN: new Set<PermissionCode>([
    'MANAGE_ALL_TENANTS',
    'MANAGE_TENANT',
    'MANAGE_USERS',
    // OPEN-037 — kept alongside MANAGE_USERS, which used to gate the roster route.
    'READ_DEVICE_ROSTER',
    // OPEN-036 / DEC-335 — end a shared tablet's session from the web.
    'CLEAR_SHARED_DEVICE',
    'IMPORT_OPERATORS_AND_SITES',
    'MANAGE_OPERATOR_PORTAL_ACCESS',
    // Phase 16 / Task 16.2 (DEC-157) — PLATFORM_ADMIN only.
    'MANAGE_DISPOSAL_PORTAL_ACCESS',
    // §58 — PLATFORM_ADMIN only.
    'MANAGE_HAULER_FEATURES',
    // Paelode team page — PLATFORM_ADMIN only.
    'MANAGE_PLATFORM_TEAM',
    'GENERATE_PLATFORM_INVOICES',
    'CREATE_JOB',
    'ASSIGN_JOB',
    'CHANGE_JOB_SITE',
    'CANCEL_JOB',
    'COMPLETE_JOB',
    'OVERRIDE_COMPLETION_PROTECTION',
    'JOB_FORCE_CLOSE',
    'CAPTURE_RUN_TICKET',
    'REVIEW_TICKET',
    'EDIT_TICKET_VOLUME',
    'VIEW_RUN_TICKETS',
    'ACKNOWLEDGE_GPS_FLAG',
    'CREATE_INVOICE',
    'VIEW_INVOICES',
    'CONNECT_QBO',
    'MANAGE_DISPOSAL_PRICES',
    // gap 1 / DEC-383
    'MANAGE_CUSTOMER_PRICES',
    // DEC-356 (#50).
    'EDIT_BILLING_TICKET',
    // OPEN-217 / DEC-382 — driver entry rules (the hauler admins and billing staff).
    'MANAGE_VALIDATION_RULES',
    'VIEW_DRIVER_MAP',
    'EDIT_GEOFENCE',
    'VIEW_AUDIT_TRAIL',
    'GENERATE_COMPLIANCE_REPORT',
    'SUBMIT_HAUL_REQUEST',
    'VIEW_TANK_LEVELS',
    // Phase 06.1 / DEC-027 — read-only platform-managed disposal_facilities catalog.
    'VIEW_DISPOSAL_FACILITIES',
    // Phase 06.2 — manage platform-managed site_types catalog + read service charges.
    'MANAGE_SITE_TYPES',
    'MANAGE_PLATFORM_CATALOG',
    // Phase 21 / Task 21.1 (DEC-327) — PLATFORM_ADMIN only.
    'MANAGE_PLATFORM_BILLING',
    // #56 (DEC-362) — the ops console. PLATFORM_ADMIN only.
    'VIEW_PLATFORM_OPS',
    'MANAGE_PLATFORM_ALERTS',
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // Phase 07 — PLATFORM_ADMIN can pull reference data for admin/testing parity (DEC-037 P3).
    'SYNC_PULL_REFERENCE_DATA',
    // Wave 3 — manage operators/sites/tanks.
    'MANAGE_REFERENCE_DATA',
    // Fleet UI (DEC-067) — manage trucks/trailers/yards + edit driver fields.
    'MANAGE_FLEET',
    // Phase 24 / Task 24.3 (DEC-094) — view + manage hauler catalog junctions.
    'VIEW_HAULER_CATALOG',
    'MANAGE_HAULER_CATALOG',
  ]),

  /**
   * TENANT_ADMIN — hauler tenant administrator.
   * §6 column 2 (TenAdmin).
   */
  TENANT_ADMIN: new Set<PermissionCode>([
    'MANAGE_TENANT',
    // DEC-326 item 7.
    'MANAGE_PAYMENT_TERMS',
    // OPEN-156.
    'VOID_BILLING_TICKET',
    'MANAGE_USERS',
    // OPEN-037 — kept alongside MANAGE_USERS, which used to gate the roster route.
    'READ_DEVICE_ROSTER',
    // OPEN-036 / DEC-335 — end a shared tablet's session from the web.
    'CLEAR_SHARED_DEVICE',
    'IMPORT_OPERATORS_AND_SITES',
    'VIEW_OPERATOR_API_CREDENTIALS',
    'CREATE_JOB',
    'ASSIGN_JOB',
    'CHANGE_JOB_SITE',
    'CANCEL_JOB',
    'COMPLETE_JOB',
    'OVERRIDE_COMPLETION_PROTECTION',
    'JOB_FORCE_CLOSE',
    'REVIEW_TICKET',
    'EDIT_TICKET_VOLUME',
    'VIEW_RUN_TICKETS',
    'ACKNOWLEDGE_GPS_FLAG',
    'CREATE_INVOICE',
    'VIEW_INVOICES',
    'CONNECT_QBO',
    'MANAGE_DISPOSAL_PRICES',
    // gap 1 / DEC-383
    'MANAGE_CUSTOMER_PRICES',
    // DEC-356 (#50).
    'EDIT_BILLING_TICKET',
    // OPEN-217 / DEC-382 — driver entry rules (the hauler admins and billing staff).
    'MANAGE_VALIDATION_RULES',
    'VIEW_DRIVER_MAP',
    'EDIT_GEOFENCE',
    'VIEW_AUDIT_TRAIL',
    'GENERATE_COMPLIANCE_REPORT',
    // Phase 06.1 / DEC-027 — tenant admins read the disposal facilities catalog.
    'VIEW_DISPOSAL_FACILITIES',
    // Phase 06.2 — tenant admins manage service charges + read site_types.
    'MANAGE_SERVICE_CHARGES',
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // Wave 3 — manage operators/sites/tanks.
    'MANAGE_REFERENCE_DATA',
    // Fleet UI (DEC-067) — manage trucks/trailers/yards + edit driver fields.
    'MANAGE_FLEET',
    // Phase 24 / Task 24.3 (DEC-094) — view + manage hauler catalog junctions.
    'VIEW_HAULER_CATALOG',
    'MANAGE_HAULER_CATALOG',
  ]),

  /**
   * DISPATCH_MANAGER — senior dispatcher with override capability.
   * §6 column 3 (DispMgr).
   */
  DISPATCH_MANAGER: new Set<PermissionCode>([
    'MANAGE_USERS',
    // OPEN-037 — kept alongside MANAGE_USERS, which used to gate the roster route.
    'READ_DEVICE_ROSTER',
    // OPEN-036 / DEC-335 — end a shared tablet's session from the web.
    'CLEAR_SHARED_DEVICE',
    'CREATE_JOB',
    'ASSIGN_JOB',
    'CHANGE_JOB_SITE',
    'CANCEL_JOB',
    'COMPLETE_JOB',
    'OVERRIDE_COMPLETION_PROTECTION',
    'JOB_FORCE_CLOSE',
    'REVIEW_TICKET',
    'VIEW_RUN_TICKETS',
    'ACKNOWLEDGE_GPS_FLAG',
    'VIEW_DRIVER_MAP',
    'EDIT_GEOFENCE',
    'VIEW_AUDIT_TRAIL',
    // Phase 06.1 / DEC-027 — dispatch managers read facilities for assignment UI.
    'VIEW_DISPOSAL_FACILITIES',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // Wave 3 — manage operators/sites/tanks (management-only; not plain DISPATCHER).
    'MANAGE_REFERENCE_DATA',
    // Fleet UI (DEC-067) — manage trucks/trailers/yards + edit driver fields.
    'MANAGE_FLEET',
    // Phase 24 / Task 24.3 (DEC-094) — view + manage hauler catalog junctions.
    'VIEW_HAULER_CATALOG',
    'MANAGE_HAULER_CATALOG',
  ]),

  /**
   * DISPATCHER — day-to-day job assignment and monitoring.
   * §6 column 4 (Disp). Cancel = ltd qualifier; audit trail = ltd; driver map included.
   */
  DISPATCHER: new Set<PermissionCode>([
    'CREATE_JOB',
    // OPEN-036 / DEC-335 — §7: "Dispatcher can force-logout from web console".
    'CLEAR_SHARED_DEVICE',
    'ASSIGN_JOB',
    'CHANGE_JOB_SITE',
    'CANCEL_JOB',
    'VIEW_RUN_TICKETS',
    'VIEW_DRIVER_MAP',
    'VIEW_AUDIT_TRAIL',
    // Phase 06.1 / DEC-027 — dispatchers read facilities for assignment UI.
    'VIEW_DISPOSAL_FACILITIES',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // Phase 24 / Task 24.3 (DEC-094) — read-only hauler catalog (reads only;
    // mutations require DISPATCH_MANAGER+ via MANAGE_HAULER_CATALOG).
    'VIEW_HAULER_CATALOG',
  ]),

  /**
   * TRUCK_PUSHER — field supervisor who reviews tickets and volume.
   * §6 column 5 (TruckPush). Driver map = ro, audit trail = ro.
   */
  TRUCK_PUSHER: new Set<PermissionCode>([
    'REVIEW_TICKET',
    // #60 / DEC-365 — the reviewer can correct the pickup site in review.
    'CHANGE_JOB_SITE',
    'EDIT_TICKET_VOLUME',
    'VIEW_RUN_TICKETS',
    'ACKNOWLEDGE_GPS_FLAG',
    'VIEW_DRIVER_MAP',
    'VIEW_AUDIT_TRAIL',
    'JOB_FORCE_CLOSE',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
  ]),

  /**
   * BILLING_MANAGER — invoicing and compliance report generation.
   * §6 column 6 (BillMgr). Audit trail = ro; compliance report = ✓.
   */
  BILLING_MANAGER: new Set<PermissionCode>([
    'REVIEW_TICKET',
    // #60 / DEC-365 — the reviewer can correct the pickup site in review (Corbin, 2026-10-16).
    'CHANGE_JOB_SITE',
    'EDIT_TICKET_VOLUME',
    'VIEW_RUN_TICKETS',
    'CREATE_INVOICE',
    'VIEW_INVOICES',
    'CONNECT_QBO',
    'MANAGE_DISPOSAL_PRICES',
    // gap 1 / DEC-383
    'MANAGE_CUSTOMER_PRICES',
    'VIEW_AUDIT_TRAIL',
    'GENERATE_COMPLIANCE_REPORT',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // DEC-326 item 7.
    'MANAGE_PAYMENT_TERMS',
    // OPEN-156.
    'VOID_BILLING_TICKET',
    // DEC-356 (#50).
    'EDIT_BILLING_TICKET',
    // OPEN-217 / DEC-382 — driver entry rules (the hauler admins and billing staff).
    'MANAGE_VALIDATION_RULES',
  ]),

  /**
   * BILLING_STAFF — invoice generation (limited) and ticket viewing.
   * §6 column 7 (BillStaff). Invoice = ltd; compliance report = ro; audit trail = ro.
   */
  BILLING_STAFF: new Set<PermissionCode>([
    'VIEW_RUN_TICKETS',
    'CREATE_INVOICE',
    'VIEW_INVOICES',
    'VIEW_AUDIT_TRAIL',
    'GENERATE_COMPLIANCE_REPORT',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // DEC-356 (#50) — edit an approved ticket's billed lines before it is invoiced.
    'EDIT_BILLING_TICKET',
    // OPEN-217 / DEC-382 — driver entry rules (the hauler admins and billing staff).
    'MANAGE_VALIDATION_RULES',
    // gap 1 / DEC-383
    'MANAGE_CUSTOMER_PRICES',
  ]),

  /**
   * DRIVER — field driver. Mobile-first. Own-records qualifiers apply.
   * §6 column 8 (Driver). Driver map = own; audit trail = own; run tickets = own.
   */
  DRIVER: new Set<PermissionCode>([
    'CREATE_JOB',
    'REJECT_JOB',
    'CAPTURE_RUN_TICKET',
    'VIEW_RUN_TICKETS',
    // VIEW_DRIVER_MAP intentionally NOT granted (Quick 260613): the driver-position
    // feed + sync-health + roster are dispatcher-console reads (Phase 9 DISPATCH-02/03);
    // a DRIVER must not enumerate other drivers' live positions. Phase 9 threat
    // T-09-01-03 assumed DRIVER lacks this permission — this aligns the matrix with it.
    //
    // The "roster" in that sentence is the DISPATCHER's roster — live positions,
    // shift state, sync health. It is NOT the shared-device driver picker, which
    // READ_DEVICE_ROSTER below covers: name + phone-last-4 + status, nothing else.
    // Granting that does not reopen what Quick 260613 closed.
    'VIEW_AUDIT_TRAIL',
    // Phase 06.1 / DEC-027 — drivers post lifecycle events + advance the
    // disposal transition + read the facilities catalog for the override picker.
    'RECORD_JOB_EVENT',
    'TRANSITION_TO_DISPOSAL',
    'VIEW_DISPOSAL_FACILITIES',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
    // Phase 07 / DEC-034 — DRIVER pushes queued offline writes (SYNC_PUSH = driver-only).
    // Phase 07 / DEC-037 — DRIVER pulls §16b reference data package.
    'SYNC_PUSH',
    'SYNC_PULL_REFERENCE_DATA',
    // OPEN-037 (Corbin 2026-10-01, option B) — a signed-in driver refreshes the
    // shared tablet's own-tenant picker list so a new hire appears and a removed
    // driver disappears. Own tenant only (req.tenantId + RLS); name + last4 only.
    'READ_DEVICE_ROSTER',
    // Phase 22 / Task 22.2 — bind/release this device's push token (own binding only).
    'REGISTER_PUSH_DEVICE',
    // Phase 22 / Task 22.9 — own push preferences only (DEC-233 mandatory set enforced).
    'MANAGE_OWN_NOTIFICATION_PREFS',
  ]),

  /**
   * OPERATOR_ADMIN — operator (E&P company) administrator.
   * §6 column 9 (OpAdmin). Compliance report = ✓; audit trail = ✓.
   */
  OPERATOR_ADMIN: new Set<PermissionCode>([
    'SUBMIT_HAUL_REQUEST',
    // Operator portal rework PR 9 (0213) — the company's API keys.
    'MANAGE_OPERATOR_API_KEYS',
    // Operator webhooks PR 1 (0239) — the company's webhook endpoints.
    'MANAGE_OPERATOR_WEBHOOKS',
    // Operator Reports (b) (0257) — the company's scheduled emailed reports.
    'MANAGE_OPERATOR_REPORT_SCHEDULES',
    // Operator portal rework PR 7 (0214) — routes and the company's tank records.
    'MANAGE_ROUTES',
    'MANAGE_OPERATOR_TANKS',
    // PR 8 (0215) — assign tanks to pumpers and foremen; the coverage flag.
    'SELECT_OPERATOR_TANKS',
    // PR 10 (0217) — the requestor → pumper matches.
    'MANAGE_REQUESTOR_LINKS',
    'VIEW_TANK_LEVELS',
    'VIEW_INVOICES',
    'VIEW_AUDIT_TRAIL',
    'GENERATE_COMPLIANCE_REPORT',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
  ]),

  /**
   * FOREMAN — operator foreman; submits haul requests, views tank levels.
   * §6 column 10 (Foreman). Compliance report = ro.
   */
  FOREMAN: new Set<PermissionCode>([
    'SUBMIT_HAUL_REQUEST',
    // Operator portal rework PR 7 (0214) — foremen build routes and select their own.
    'MANAGE_ROUTES',
    // PR 8 (0215) — select their own tanks and their pumpers'.
    'SELECT_OPERATOR_TANKS',
    'VIEW_TANK_LEVELS',
    'GENERATE_COMPLIANCE_REPORT',
    // Phase 06.2 — read site_types for job detail + view service charges.
    'READ_SITE_TYPES',
    'VIEW_SERVICE_CHARGES',
  ]),

  /**
   * PUMPER — operator field user (operator portal rework, migration 0200). The portal's reads
   * are gated by plan and, from PR 8, by the tanks the pumper selected — not by permissions — so
   * this holds only the two catalog reads a ticket or tank view needs. Haul requests come with
   * their own PR (design §1.7).
   */
  PUMPER: new Set<PermissionCode>([
    'VIEW_TANK_LEVELS',
    'READ_SITE_TYPES',
    // PR 8 (0215) — choose the tanks they look after (and so see).
    'SELECT_OPERATOR_TANKS',
    // PR 11c (0221) — the phone's queued tank picks, sent when it finds signal.
    'OPERATOR_SYNC_PUSH',
    // PR 12a (0222) — Corbin: pumpers turn in loads, for the tanks they look after.
    'SUBMIT_HAUL_REQUEST',
  ]),

  /**
   * DISPOSAL_ADMIN — DISPOSAL tenant administrator. Created via Task 16.2's
   * registration-approval flow. Grants stack as future disposal-portal
   * modules ship (§27 ticket view, §49 module toggles, etc.). 16.5 adds
   * MANAGE_DISPOSAL_REPORTS on top of 16.4's daily-email recipient grant.
   */
  DISPOSAL_ADMIN: new Set<PermissionCode>([
    'MANAGE_DISPOSAL_EMAIL_RECIPIENTS',
    // Phase 16 / Task 16.5 (DEC-162) — custom report templates + runs + recipients.
    'MANAGE_DISPOSAL_REPORTS',
    // Phase 16 / Task 16.6 (DEC-163) — BI panel reads.
    'VIEW_DISPOSAL_BI_PANELS',
  ]),
} as const satisfies Record<SingleRoleCode, ReadonlySet<PermissionCode>>;

/**
 * DEC-313 — which two single roles each combined role is made of.
 *
 * This is the definition of record: the Sets below are built from it by
 * spreading, and `permissions.test.ts` asserts each combined Set equals exactly
 * the union of these two components — no extra, no missing. So a component
 * gaining a permission later propagates automatically, which is the whole
 * reason the unions are not written out by hand.
 */
export const COMBINED_ROLE_COMPONENTS = {
  DISPATCHER_BILLING_STAFF: ['DISPATCHER', 'BILLING_STAFF'],
  DISPATCHER_BILLING_MANAGER: ['DISPATCHER', 'BILLING_MANAGER'],
  DISPATCH_MANAGER_BILLING_STAFF: ['DISPATCH_MANAGER', 'BILLING_STAFF'],
  DISPATCH_MANAGER_BILLING_MANAGER: ['DISPATCH_MANAGER', 'BILLING_MANAGER'],
} as const satisfies Record<CombinedRoleCode, readonly [SingleRoleCode, SingleRoleCode]>;

/** The exact union of a combined role's two components. */
function unionOf(role: CombinedRoleCode): ReadonlySet<PermissionCode> {
  const [a, b] = COMBINED_ROLE_COMPONENTS[role];
  return new Set<PermissionCode>([...SINGLE_ROLE_PERMISSIONS[a], ...SINGLE_ROLE_PERMISSIONS[b]]);
}

/**
 * ROLE_PERMISSIONS — the eleven authored §6 roles plus the four DEC-313
 * combined roles, whose Sets are derived unions.
 *
 * The `as const satisfies Record<RoleCode, …>` is what makes adding a RoleCode
 * a compile error until this map covers it.
 *
 * What a union does NOT do is invent access: no combined role holds a
 * permission neither component holds. What it DOES do is put both components'
 * access in one pair of hands, and for the two MANAGER combinations that spans
 * create-job -> approve-ticket -> edit-volume -> invoice. Corbin accepted that
 * explicitly on 2026-10-09 with the audit trail as the compensating control;
 * DEC-313 records which audit row each of those four actions writes.
 */
export const ROLE_PERMISSIONS = {
  ...SINGLE_ROLE_PERMISSIONS,
  DISPATCHER_BILLING_STAFF: unionOf('DISPATCHER_BILLING_STAFF'),
  DISPATCHER_BILLING_MANAGER: unionOf('DISPATCHER_BILLING_MANAGER'),
  DISPATCH_MANAGER_BILLING_STAFF: unionOf('DISPATCH_MANAGER_BILLING_STAFF'),
  DISPATCH_MANAGER_BILLING_MANAGER: unionOf('DISPATCH_MANAGER_BILLING_MANAGER'),
} as const satisfies Record<RoleCode, ReadonlySet<PermissionCode>>;

/**
 * Roles whose grant includes a BILLING component — the `actor_type = 'BILLING'`
 * set for invoice audit rows (master spec §32's six-value CHECK).
 *
 * TENANT_ADMIN and PLATFORM_ADMIN also hold CREATE_INVOICE but have always been
 * recorded as 'DISPATCHER' there, and this is not the change that revisits that.
 */
export const ROLES_WITH_BILLING_COMPONENT: ReadonlySet<RoleCode> = new Set<RoleCode>([
  'BILLING_MANAGER',
  'BILLING_STAFF',
  ...COMBINED_ROLE_CODES,
]);

/**
 * D-07 — PermissionPredicate shape for route-level qualifiers (ltd/ro/own).
 *
 * Phase 3 ships the type; Plan 5+ provides concrete predicates per route.
 * `ro` is cheapest — implemented as HTTP method allowlist on the route option.
 * `ltd`/`own` use either an in-memory row predicate OR a SQL factory appended
 * to the query's WHERE clause.
 */
export type PermissionPredicate =
  | ((req: { userId: string; tenantId: string; roleCode: RoleCode }, row: unknown) => boolean)
  | {
      kind: 'sql';
      build: (req: { userId: string; tenantId: string; roleCode: RoleCode }) => unknown;
    };

/**
 * D-07 — PermissionResult discriminated union.
 *
 * `allowed: true` — full unconditional access.
 * `allowed: 'with-predicate'` — access gated by a row-level or SQL predicate.
 * `allowed: false` — denied; reason goes to Pino warn log (D-09), never to client.
 */
export type PermissionResult =
  | { allowed: true }
  | { allowed: 'with-predicate'; predicate: PermissionPredicate }
  | { allowed: false; reason: string };
